'use client'

import { useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { Toast } from '@skop-app/lib-front'
import { useQueryClient } from '@tanstack/react-query'

import {
  setImpersonationCookie,
  stopImpersonation as stopImpersonationAction,
} from '@/app/actions/impersonation'
import { clearQueryClientAuthStateBestEffort } from '@/services/auth/queryClientCleanup.client'
import { emitImpersonationSync } from '@/services/auth/impersonationSync.client'
import { captureSessionEvent } from '@/services/auth/sessionTelemetry.client'
import { SELECTED_TEAM_COOKIE_NAME } from '@/features/team-selection/constants'
import { emitCookieChange } from '@/utils/cookies/clientCookies'
import { runWithSelectedTeamSessionLock } from '@/features/team-selection/lib/selectionLock'

type StopImpersonationOptions = {
  reason?: string
  silent?: boolean
}

export function useImpersonationControls() {
  const queryClient = useQueryClient()
  const router = useRouter()

  const refreshSessionScope = useCallback(
    async (reason: string) => {
      await clearQueryClientAuthStateBestEffort(queryClient)
      emitCookieChange(SELECTED_TEAM_COOKIE_NAME)
      emitImpersonationSync(reason)
      router.refresh()
    },
    [queryClient, router],
  )

  const startImpersonation = useCallback(
    (userId: string): Promise<boolean> =>
      runWithSelectedTeamSessionLock(async () => {
        try {
          await setImpersonationCookie(userId)
          captureSessionEvent('session_scope_rotated', 'impersonation_start')
        } catch (error) {
          captureSessionEvent('impersonation_denied', 'server_policy')
          Toast.error(
            error instanceof Error
              ? error.message
              : 'Unable to start impersonation.',
          )
          return false
        }

        await refreshSessionScope('impersonation_start')
        return true
      }),
    [refreshSessionScope],
  )

  const stopImpersonation = useCallback(
    (options?: StopImpersonationOptions): Promise<boolean> =>
      runWithSelectedTeamSessionLock(async () => {
        const reason = options?.reason ?? 'impersonation_stop'

        try {
          await stopImpersonationAction(reason)
          captureSessionEvent('session_scope_rotated', reason)
        } catch (error) {
          if (!options?.silent) {
            Toast.error(
              error instanceof Error
                ? error.message
                : 'Unable to stop impersonation.',
            )
          }
          return false
        }

        await refreshSessionScope(reason)
        return true
      }),
    [refreshSessionScope],
  )

  return {
    startImpersonation,
    stopImpersonation,
  }
}
